import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { messagesAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { exportMessagesToJSON, exportMessagesToCSV } from '../utils/exportUtils'
import MessageList from '../components/MessageList'
import MessageDetail from '../components/MessageDetail'
import './Messages.css'

// Use relative WebSocket URL for Docker/production, full URL for development
const WS_URL = import.meta.env.VITE_WS_URL ||
  (window.location.protocol === 'https:' ? 'wss://' : 'ws://') + window.location.host

function Messages() {
  const { isAdmin, token } = useAuth()
  const [messages, setMessages] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [liveUpdates, setLiveUpdates] = useState(true)
  const [wsConnected, setWsConnected] = useState(false)
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [splitPosition, setSplitPosition] = useState(() => {
    const saved = localStorage.getItem('messagesSplitPosition')
    return saved ? parseInt(saved, 10) : 50
  })
  const [isResizing, setIsResizing] = useState(false)
  const [filters, setFilters] = useState({
    direction: 'all',
    stationId: '',
    messageType: 'all',
    action: '',
    searchQuery: '',
    limit: 100,
  })

  const wsRef = useRef(null)
  const messagesEndRef = useRef(null)
  const exportMenuRef = useRef(null)
  const splitContainerRef = useRef(null)

  // Helper function to format payload for search
  const getSearchablePayload = (payload) => {
    if (!payload) return ''
    try {
      return JSON.stringify(payload)
    } catch {
      return String(payload)
    }
  }

  // Filter messages based on search and filters
  const filteredMessages = useMemo(() => {
    return messages.filter((message) => {
      // Message type filter
      if (filters.messageType !== 'all' && message.messageType !== filters.messageType) {
        return false
      }

      // Action filter
      if (filters.action && message.action && !message.action.toLowerCase().includes(filters.action.toLowerCase())) {
        return false
      }

      // Search query - search in multiple fields
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase()
        const searchableText = [
          message.stationId,
          message.messageType,
          message.action,
          message.messageId,
          message.errorCode,
          message.errorDescription,
          getSearchablePayload(message.payload)
        ].join(' ').toLowerCase()

        if (!searchableText.includes(query)) {
          return false
        }
      }

      return true
    })
  }, [messages, filters.messageType, filters.action, filters.searchQuery])

  const messageCounts = useMemo(() => {
    return filteredMessages.reduce(
      (acc, message) => {
        acc.total += 1
        if (message.direction === 'sent') {
          acc.sent += 1
        } else if (message.direction === 'received') {
          acc.received += 1
        }
        return acc
      },
      { total: 0, sent: 0, received: 0 }
    )
  }, [filteredMessages])

  const displayStats = useMemo(() => {
    const chooseValue = (statValue, fallback) => {
      if (statValue === undefined || statValue === null) {
        return fallback
      }
      return Math.max(statValue, fallback)
    }

    return {
      total: chooseValue(stats?.total, messageCounts.total),
      sent: chooseValue(stats?.sent, messageCounts.sent),
      received: chooseValue(stats?.received, messageCounts.received),
      buffered: stats?.buffered ?? 0,
      dropped: stats?.dropped ?? 0,
    }
  }, [stats, messageCounts])

  const normalizeMessage = (message) => ({
    stationId: message.stationId ?? message.StationID ?? '',
    direction: message.direction ?? message.Direction ?? '',
    messageType: message.messageType ?? message.MessageType ?? '',
    action: message.action ?? message.Action ?? '',
    messageId: message.messageId ?? message.MessageID ?? '',
    protocolVersion: message.protocolVersion ?? message.ProtocolVersion ?? '',
    payload: message.payload ?? message.Payload ?? null,
    timestamp: message.timestamp ?? message.Timestamp ?? null,
    correlationId: message.correlationId ?? message.CorrelationID ?? '',
    errorCode: message.errorCode ?? message.ErrorCode ?? '',
    errorDescription:
      message.errorDescription ??
      message.ErrorDescription ??
      message.ErrorDesc ??
      message.errorDesc ??
      '',
  })

  useEffect(() => {
    fetchMessages()
    fetchStats()
  }, [filters])

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false)
      }
    }

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showExportMenu])

  // Use ref to track if we should reconnect (avoids stale closure)
  const liveUpdatesRef = useRef(liveUpdates)
  const tokenRef = useRef(token)
  const stationIdRef = useRef(filters.stationId)
  const reconnectTimeoutRef = useRef(null)

  // Keep refs in sync with state
  useEffect(() => {
    liveUpdatesRef.current = liveUpdates
  }, [liveUpdates])

  useEffect(() => {
    tokenRef.current = token
  }, [token])

  useEffect(() => {
    stationIdRef.current = filters.stationId
  }, [filters.stationId])

  // Connect WebSocket using refs to avoid stale closures
  const connectWebSocket = useCallback(() => {
    // Clear any pending reconnect
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }

    if (!liveUpdatesRef.current || !tokenRef.current) {
      return
    }

    try {
      // Build WebSocket URL with filters
      let wsUrl = `${WS_URL}/api/ws/messages`
      const params = new URLSearchParams()
      params.append('token', tokenRef.current)
      if (stationIdRef.current) {
        params.append('stationId', stationIdRef.current)
      }
      wsUrl += `?${params.toString()}`

      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        console.log('WebSocket connected')
        setWsConnected(true)
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)

          if (data.type === 'welcome') {
            console.log('Received welcome message:', data.message)
          } else if (data.type === 'ocpp_message') {
            // Inline message handling to avoid stale closure
            const message = data.message
            const formattedMessage = normalizeMessage(message)

            setMessages((prev) => {
              const newMessages = [formattedMessage, ...prev]
              if (newMessages.length > 100) {
                return newMessages.slice(0, 100)
              }
              return newMessages
            })

            setStats((prev) => {
              if (!prev) return prev
              return {
                ...prev,
                total: (prev.total || 0) + 1,
                sent: formattedMessage.direction === 'sent' ? (prev.sent || 0) + 1 : prev.sent,
                received: formattedMessage.direction === 'received' ? (prev.received || 0) + 1 : prev.received,
              }
            })
          }
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err)
        }
      }

      ws.onerror = (error) => {
        console.error('WebSocket error:', error)
        setWsConnected(false)
      }

      ws.onclose = () => {
        console.log('WebSocket closed')
        setWsConnected(false)

        // Attempt to reconnect after 5 seconds if live updates are still enabled
        if (liveUpdatesRef.current) {
          reconnectTimeoutRef.current = setTimeout(() => {
            if (liveUpdatesRef.current) {
              connectWebSocket()
            }
          }, 5000)
        }
      }
    } catch (err) {
      console.error('Failed to create WebSocket:', err)
    }
  }, [])

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!liveUpdates) {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
      setWsConnected(false)
      return
    }

    connectWebSocket()

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [liveUpdates, filters.stationId, token, connectWebSocket])

  const fetchMessages = async () => {
    try {
      const params = {}
      if (filters.stationId) params.stationId = filters.stationId
      if (filters.direction !== 'all') params.direction = filters.direction
      params.limit = filters.limit

      const response = await messagesAPI.getAll(params)
      const normalized = (response.data.messages || []).map((message) => normalizeMessage(message))
      setMessages(normalized)
      setError(null)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await messagesAPI.getStats()
      setStats(response.data)
    } catch (err) {
      console.error('Failed to fetch stats:', err)
    }
  }

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  const handleClearMessages = async () => {
    if (!confirm('Are you sure you want to clear all messages?')) {
      return
    }

    try {
      await messagesAPI.clear()
      fetchMessages()
      fetchStats()
    } catch (err) {
      alert(`Failed to clear messages: ${err.message}`)
    }
  }

  // Get unique actions from messages for autocomplete
  const uniqueActions = useMemo(() => {
    const actions = new Set()
    messages.forEach((message) => {
      if (message.action) {
        actions.add(message.action)
      }
    })
    return Array.from(actions).sort()
  }, [messages])

  const handleClearFilters = () => {
    setFilters({
      direction: 'all',
      stationId: '',
      messageType: 'all',
      action: '',
      searchQuery: '',
      limit: 100,
    })
  }

  // Resize handlers for split panel
  const handleResizeStart = useCallback((e) => {
    e.preventDefault()
    setIsResizing(true)
  }, [])

  const handleResizeMove = useCallback((e) => {
    if (!isResizing || !splitContainerRef.current) return

    const container = splitContainerRef.current
    const containerRect = container.getBoundingClientRect()
    const newPosition = ((e.clientX - containerRect.left) / containerRect.width) * 100

    // Clamp between 30% and 70%
    const clampedPosition = Math.min(Math.max(newPosition, 30), 70)
    setSplitPosition(clampedPosition)
  }, [isResizing])

  const handleResizeEnd = useCallback(() => {
    if (isResizing) {
      setIsResizing(false)
      localStorage.setItem('messagesSplitPosition', splitPosition.toString())
    }
  }, [isResizing, splitPosition])

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove)
      document.addEventListener('mouseup', handleResizeEnd)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    }

    return () => {
      document.removeEventListener('mousemove', handleResizeMove)
      document.removeEventListener('mouseup', handleResizeEnd)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing, handleResizeMove, handleResizeEnd])

  const handleMessageSelect = (message, index) => {
    setSelectedMessage({ ...message, index })
  }

  const hasActiveFilters = () => {
    return (
      filters.direction !== 'all' ||
      filters.stationId !== '' ||
      filters.messageType !== 'all' ||
      filters.action !== '' ||
      filters.searchQuery !== ''
    )
  }

  const handleExport = (format) => {
    if (filteredMessages.length === 0) {
      alert('No messages to export')
      return
    }

    if (format === 'json') {
      exportMessagesToJSON(filteredMessages)
    } else if (format === 'csv') {
      exportMessagesToCSV(filteredMessages)
    }
  }

  if (loading) {
    return <div className="loading">Loading messages...</div>
  }

  if (error) {
    return <div className="error">Error loading messages: {error}</div>
  }

  return (
    <div className="messages messages--desktop">
      {/* Fixed Header Bar */}
      <div className="messages-header">
        <div className="messages-header__top">
          <h2>OCPP Messages</h2>
          <div className="header-actions">
            <div className="live-updates-toggle">
              <label>
                <input
                  type="checkbox"
                  checked={liveUpdates}
                  onChange={(e) => setLiveUpdates(e.target.checked)}
                />
                <span>Live</span>
                {wsConnected && <span className="ws-indicator connected">●</span>}
                {!wsConnected && liveUpdates && <span className="ws-indicator disconnected">●</span>}
              </label>
            </div>

            {/* Inline Stats */}
            {(stats || messageCounts.total > 0) && (
              <div className="stats-inline">
                <span className="stat-inline">
                  <span className="stat-inline__value">{displayStats.total}</span>
                  <span className="stat-inline__label">total</span>
                </span>
                <span className="stat-inline stat-inline--sent">
                  <span className="stat-inline__value">{displayStats.sent}</span>
                  <span className="stat-inline__label">sent</span>
                </span>
                <span className="stat-inline stat-inline--received">
                  <span className="stat-inline__value">{displayStats.received}</span>
                  <span className="stat-inline__label">recv</span>
                </span>
              </div>
            )}

            <div className="export-dropdown" ref={exportMenuRef}>
              <button
                className="btn btn--sm btn--secondary"
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={filteredMessages.length === 0}
              >
                Export
              </button>
              {showExportMenu && (
                <div className="export-menu">
                  <button onClick={() => { handleExport('json'); setShowExportMenu(false); }}>
                    Export as JSON
                  </button>
                  <button onClick={() => { handleExport('csv'); setShowExportMenu(false); }}>
                    Export as CSV
                  </button>
                </div>
              )}
            </div>

            {isAdmin && (
              <button className="btn btn--sm btn--danger" onClick={handleClearMessages}>
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Compact Filter Bar */}
        <div className="filters-bar">
          <input
            type="text"
            className="search-input search-input--compact"
            placeholder="Search messages..."
            value={filters.searchQuery}
            onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
          />

          <div className="filters-inline">
            <select
              className="filter-select"
              value={filters.direction}
              onChange={(e) => handleFilterChange('direction', e.target.value)}
            >
              <option value="all">All Directions</option>
              <option value="sent">Sent</option>
              <option value="received">Received</option>
            </select>

            <select
              className="filter-select"
              value={filters.messageType}
              onChange={(e) => handleFilterChange('messageType', e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="Call">Call</option>
              <option value="CallResult">CallResult</option>
              <option value="CallError">CallError</option>
            </select>

            <input
              type="text"
              className="filter-input"
              list="action-suggestions"
              placeholder="Action..."
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
            />
            <datalist id="action-suggestions">
              {uniqueActions.map((action) => (
                <option key={action} value={action} />
              ))}
            </datalist>

            <input
              type="text"
              className="filter-input"
              placeholder="Station ID..."
              value={filters.stationId}
              onChange={(e) => handleFilterChange('stationId', e.target.value)}
            />

            <select
              className="filter-select filter-select--sm"
              value={filters.limit}
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
            >
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
              <option value="500">500</option>
            </select>

            {hasActiveFilters() && (
              <button className="btn-clear-filters--sm" onClick={handleClearFilters}>
                Clear
              </button>
            )}
          </div>

          <div className="results-count">
            {filteredMessages.length} / {messages.length}
          </div>
        </div>
      </div>

      {/* Split Panel Content */}
      <div className="messages-content" ref={splitContainerRef}>
        {/* Message List Panel */}
        <div
          className="messages-list-panel"
          style={{ width: `${splitPosition}%` }}
        >
          <MessageList
            messages={messages}
            filteredMessages={filteredMessages}
            selectedIndex={selectedMessage?.index}
            onSelect={handleMessageSelect}
            hasActiveFilters={hasActiveFilters()}
            onClearFilters={handleClearFilters}
          />
        </div>

        {/* Resize Handle */}
        <div
          className={`resize-handle ${isResizing ? 'resizing' : ''}`}
          onMouseDown={handleResizeStart}
        >
          <div className="resize-handle__grip" />
        </div>

        {/* Message Detail Panel */}
        <div
          className="messages-detail-panel"
          style={{ width: `${100 - splitPosition}%` }}
        >
          <MessageDetail message={selectedMessage} onClose={() => setSelectedMessage(null)} />
        </div>
      </div>
    </div>
  )
}

export default Messages
